import {
  askConfirm, dateFieldHtml, escapeHtml, flagSvg, mountDateField, notchedFieldHtml,
  type MountedDateField,
} from '@colplat/ui-web';
import { apiFetch, failureMessage } from '../api.js';
import { knownOffline, readCached } from '../offlineReads.js';
import { appHref } from '../globals.js';
import { bootstrapWebledgerClient, webledgerShellHtml, wireBackButton, type WebledgerClient } from '../webledgerClient.js';
import { accountPath } from './webledgerAccountTree.js';
import { inlineAccountIcon } from './webledgerAccountDisplay.js';
import { openAccountPicker } from './webledgerAccountPicker.js';
import type { AccountDto } from './webledgerOfflineState.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

/**
 * Bank sync — legacy's `banksync.php` + `assets/banksync.js`.
 *
 * Connect a bank over PSD2 account information (read-only), map each feed
 * account to a ledger account, and pull booked entries in. Nothing here can
 * move money; there is no payment-initiation path in this codebase.
 *
 * This is the one webledger page that genuinely **needs the network**. Every
 * entity behind it is `offline: 'none'`, and rightly so: a consent flow needs
 * the bank, and a sync is a network round trip by definition. So it says so
 * plainly when there is no connection rather than rendering an empty shell —
 * the opposite of the other pages, which are cache-first.
 *
 * The consent callback lands *here*: the bank redirects to
 * `/webledger/bank/callback?code=…&state=…`, which routes to this same page
 * (main.ts), and the first thing it does is exchange that code under the
 * user's own session. See the note on EB_REDIRECT_URL in ops/env — colplat
 * authenticates with a bearer token in a header, so a server-side callback
 * would arrive with no credentials at all.
 */

/** Legacy's list: the SEPA countries plus the UK, which is what Enable Banking covers. */
const COUNTRIES: Array<[string, string]> = [
  ['HU', 'Hungary'], ['AT', 'Austria'], ['BE', 'Belgium'], ['BG', 'Bulgaria'], ['HR', 'Croatia'],
  ['CY', 'Cyprus'], ['CZ', 'Czechia'], ['DK', 'Denmark'], ['EE', 'Estonia'], ['FI', 'Finland'],
  ['FR', 'France'], ['DE', 'Germany'], ['GR', 'Greece'], ['IS', 'Iceland'], ['IE', 'Ireland'],
  ['IT', 'Italy'], ['LV', 'Latvia'], ['LI', 'Liechtenstein'], ['LT', 'Lithuania'], ['LU', 'Luxembourg'],
  ['MT', 'Malta'], ['NL', 'Netherlands'], ['NO', 'Norway'], ['PL', 'Poland'], ['PT', 'Portugal'],
  ['RO', 'Romania'], ['SK', 'Slovakia'], ['SI', 'Slovenia'], ['ES', 'Spain'], ['SE', 'Sweden'],
  ['GB', 'United Kingdom'],
];

const MATCH_FIELD_LABELS: Record<string, string> = {
  any: 'Description or party', description: 'Description', counterparty: 'Counterparty',
};

interface BankAccountDto {
  id: string; connectionId: string; ebAccountUid: string;
  iban: string | null; name: string | null; currency: string | null;
  ledgerAccountId: string | null; suspenseAccountId: string | null;
  importFrom: string | null; lastSynced: string | null; lastTxnDate: string | null;
}
interface ConnectionDto {
  id: string; aspspName: string; aspspCountry: string; psuType: string | null;
  status: string; errorMsg: string | null; authorizedAt: string | null; expiresAt: string | null;
  /** Fetches spent on this connection since midnight — the allowance is per connection, not per account. */
  syncsToday: number;
  accounts: BankAccountDto[];
}
interface ConnectionsResponse {
  connections: ConnectionDto[]; syncLimit: number; consentWarnDays: number; enabled: boolean;
}
interface RuleDto {
  id: string; matchField: string; matchType: string; pattern: string;
  targetAccountId: string; priority: number; enabled: boolean; accountName: string | null;
}
interface SyncOutcomeDto {
  bankAccountId: string; label: string; status: string;
  added: number; skipped: number; enriched: number; message: string;
}
interface SyncResponse {
  results: SyncOutcomeDto[];
  summary: {
    accounts: number; added: number; skipped: number; enriched: number;
    capped: number; expired: number; errors: number;
  };
}

export async function mountWebledgerBankSync(root: HTMLElement): Promise<void> {
  root.innerHTML = shellHtml();
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('#bsConnBar')!, { onPage: true });

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  const isAdmin = client.roles.includes('webledger-admin');

  // Before anything is fetched: a callback carries a one-time code in the
  // address bar, and the sooner that is spent and scrubbed the better.
  await handleCallback(root, client);

  const accountsRead = await readCached<{ accounts: AccountDto[] }>(
    client.docCache, 'accounts', '/apps/webledger/accounts', client.token,
  );
  const accounts = accountsRead.ok ? accountsRead.data.accounts : [];

  const page = new BankPage(root, client, accounts, isAdmin);
  await page.load();
}

// ------------------------------------------------------------ the callback

/**
 * Finish a consent, if this page load is a return from the bank.
 *
 * The code is exchanged and then the query string is scrubbed from the URL
 * before anything else happens. A one-time authorisation code sitting in the
 * address bar gets into browser history, into a bookmark, into the next
 * screenshot someone pastes into a bug report — and a reload would replay it
 * and fail confusingly. `replaceState` also means Back does not walk into the
 * spent code.
 */
async function handleCallback(root: HTMLElement, client: WebledgerClient): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code') ?? '';
  const state = params.get('state') ?? '';
  const error = params.get('error') ?? params.get('error_description') ?? '';
  if (!code && !error) return;

  const banner = root.querySelector<HTMLElement>('#bsCallback')!;
  banner.hidden = false;
  banner.className = 'alert alert-secondary py-2 small';
  banner.textContent = 'Finishing the connection with your bank…';

  const res = await apiFetch('/apps/webledger/bank/callback', {
    method: 'POST', token: client.token, body: { code, state, error },
  }).catch(() => undefined);

  window.history.replaceState({}, '', appHref('/webledger/bank'));

  if (res?.ok) {
    const { accounts } = (await res.json()) as { accounts?: number };
    const n = accounts ?? 0;
    banner.className = 'alert alert-success py-2 small';
    banner.textContent = `Bank connected. ${n} account${n === 1 ? '' : 's'} discovered — map ${n === 1 ? 'it' : 'them'} to a ledger account below, then sync.`;
    client.notify('Bank connected.');
    return;
  }
  banner.className = 'alert alert-danger py-2 small';
  banner.textContent = await failureMessage(res, 'The connection could not be completed.');
}

// ------------------------------------------------------------------- shell

/**
 * `#bsSyncResult` is deliberately *not* a live region.
 *
 * `bootstrapWebledgerClient` binds `notify` to the first `[aria-live]` in the
 * page, so a second one here captured every notice — each one written in as
 * `textContent`, wiping whatever sync result the panel was showing. The
 * screen-reader region at the end is the one that should get them, and it is
 * the same `cf-sr` element every other webledger page uses.
 */
function shellHtml(): string {
  return webledgerShellHtml('bank', `
    <div class="alert py-2 small" id="bsCallback" role="status" hidden></div>
    <div id="bsConnectCard"></div>
    <div class="alert py-2 small" id="bsNotice" hidden></div>
    <div id="bsSyncResult"></div>
    <div class="d-flex justify-content-between align-items-center mb-2" id="bsConnBar">
      <div class="cf-h">Connections</div>
      <button class="btn btn-sm btn-primary" type="button" id="bsSyncAll" hidden>Sync all mapped accounts</button>
    </div>
    <div id="bsConnections"><p class="text-secondary small">Loading…</p></div>
    <div class="cf-card cf-card--bleed p-3 mt-4" id="bsRulesCard">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="cf-h">Auto-categorisation rules</div>
        <button class="btn btn-sm btn-outline-secondary" type="button" id="bsApplyRules">Apply to uncategorised</button>
      </div>
      <p class="text-secondary small">
        When an imported transaction's text matches a rule, its other leg is booked to the chosen account
        instead of the uncategorised account. Rules are checked top to bottom by priority; the first match wins.
      </p>
      <div id="bsRules"><p class="text-secondary small mb-0">Loading…</p></div>
      <div id="bsRuleForm" class="mt-3"></div>
    </div>
    <div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`);
}

function connectCardHtml(): string {
  const countries = COUNTRIES.map(([code, name]) =>
    `<option value="${code}"${code === 'HU' ? ' selected' : ''}>${escapeHtml(name)} (${code})</option>`).join('');
  return `
    <div class="cf-card cf-card--bleed p-3 mb-4">
      <div class="cf-h mb-2">Connect a bank</div>
      <div class="row g-2 align-items-end">
        <div class="col-sm-3">
          <label class="form-label mb-1 small" for="bsCountry">Country</label>
          <select class="form-select form-select-sm" id="bsCountry">${countries}</select>
        </div>
        <div class="col-sm-2">
          <label class="form-label mb-1 small" for="bsPsu">Account type</label>
          <select class="form-select form-select-sm" id="bsPsu">
            <option value="personal" selected>Personal</option>
            <option value="business">Business</option>
          </select>
        </div>
        <div class="col-sm-auto">
          <button class="btn btn-outline-secondary btn-sm" type="button" id="bsListBanks">List banks</button>
        </div>
        <div class="col-sm-4">
          <label class="form-label mb-1 small" for="bsBank">Bank</label>
          <select class="form-select form-select-sm" id="bsBank" disabled>
            <option value="">List banks first…</option>
          </select>
        </div>
        <div class="col-sm-auto">
          <button class="btn btn-primary btn-sm" type="button" id="bsConnect" disabled>Connect</button>
        </div>
      </div>
      <p class="text-secondary small mb-0 mt-2">
        You will be sent to your bank to approve <strong>read-only</strong> access to your account
        information, then returned here to map the accounts. Nothing in this app can move money.
      </p>
    </div>`;
}

// -------------------------------------------------------------- the page

class BankPage {
  private connections: ConnectionDto[] = [];
  /** One per rendered "not before" field, disposed and rebuilt with the list. */
  private dateFields: MountedDateField[] = [];
  private rules: RuleDto[] = [];
  private syncLimit = 4;
  private consentWarnDays = 14;
  /** The rule the form is editing, or `undefined` when it is an add form. */
  private editingRule: RuleDto | undefined;
  /** Chosen in the rule form before it is saved — the form's own state, not a rule's. */
  private ruleTargetId = '';

  constructor(
    private readonly root: HTMLElement,
    private readonly client: WebledgerClient,
    private readonly accounts: readonly AccountDto[],
    private readonly isAdmin: boolean,
  ) {}

  async load(): Promise<void> {
    if (this.isAdmin) {
      this.root.querySelector<HTMLElement>('#bsConnectCard')!.innerHTML = connectCardHtml();
      this.wireConnectForm();
    }
    this.root.querySelector<HTMLElement>('#bsApplyRules')!.hidden = !this.isAdmin;
    this.wireSyncAll();
    await Promise.all([this.refreshConnections(), this.refreshRules()]);
  }

  /**
   * Say something where the person is actually looking.
   *
   * `client.notify` writes into the shared `.cf-sr` region, which is
   * `width:1px;clip:rect(0 0 0 0)` — a screen-reader announcement and nothing
   * else. Every failure on this page used to go there and only there, so a
   * Connect that the bank refused looked exactly like a button that did
   * nothing at all. Reported as "pressing connect does nothing", and that is
   * precisely what it was.
   *
   * Deliberately *not* an `aria-live` element itself: `bootstrapWebledgerClient`
   * binds `notify` to the first `[aria-live]` in the page, and a second one
   * here would both capture those announcements and read every message twice.
   * The visible element shows, the shared region announces, once each.
   */
  private notice(message: string, level: 'danger' | 'success' = 'danger'): void {
    const el = this.root.querySelector<HTMLElement>('#bsNotice')!;
    el.hidden = false;
    el.className = `alert alert-${level} py-2 small`;
    el.textContent = message;
    // A failure reported at the top of a long page is no better than a silent
    // one when the button that caused it is three screens down.
    el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    this.client.notify(message);
  }

  private clearNotice(): void {
    const el = this.root.querySelector<HTMLElement>('#bsNotice')!;
    el.hidden = true;
    el.textContent = '';
  }

  /** Announce without showing — for progress and for things the page itself already makes obvious. */
  private notify(message: string): void {
    this.client.notify(message);
  }

  // ------------------------------------------------------------ connections

  private async refreshConnections(): Promise<void> {
    const container = this.root.querySelector<HTMLElement>('#bsConnections')!;
    if (knownOffline()) {
      container.innerHTML = offlineNoticeHtml();
      this.root.querySelector<HTMLElement>('#bsSyncAll')!.hidden = true;
      return;
    }
    const res = await apiFetch('/apps/webledger/bank/connections', { token: this.client.token })
      .catch(() => undefined);
    if (!res) {
      container.innerHTML = offlineNoticeHtml();
      return;
    }
    if (!res.ok) {
      container.innerHTML = `<div class="alert alert-danger py-2 small mb-0">${escapeHtml(
        res.status === 403 ? 'You do not have access to this app.' : 'Could not load bank connections.')}</div>`;
      return;
    }
    const data = (await res.json()) as ConnectionsResponse;
    this.syncLimit = data.syncLimit;
    this.consentWarnDays = data.consentWarnDays;
    this.connections = data.connections;

    if (!data.enabled) {
      // The tab is only offered when the server says bank sync is configured,
      // so this is the narrow window where it was turned off since the page
      // was served. Saying which server setting it is saves a support round.
      container.innerHTML = '<div class="alert alert-secondary py-2 small mb-0">'
        + 'Bank sync is not enabled on this server (<code>EB_ENABLED</code>).</div>';
      this.root.querySelector<HTMLElement>('#bsConnectCard')!.innerHTML = '';
      this.root.querySelector<HTMLElement>('#bsRulesCard')!.hidden = true;
      return;
    }
    this.renderConnections();
  }

  private renderConnections(): void {
    const container = this.root.querySelector<HTMLElement>('#bsConnections')!;
    // Offered only while at least one connection both has something mapped
    // and has a fetch left — otherwise the button is an invitation to spend a
    // round trip discovering it is capped.
    const anySyncable = this.connections.some(
      (c) => c.accounts.some(isMapped) && c.syncsToday < this.syncLimit,
    );
    this.root.querySelector<HTMLElement>('#bsSyncAll')!.hidden = !anySyncable;

    if (this.connections.length === 0) {
      container.innerHTML = '<p class="text-secondary small">'
        + (this.isAdmin
          ? 'No banks connected yet. Use “Connect a bank” above.'
          : 'No banks are connected yet. An administrator can connect one.')
        + '</p>';
      return;
    }
    container.innerHTML = this.connections.map((c) => this.connectionHtml(c)).join('');
    this.mountDateFields();
    this.wireConnections();
  }

  /**
   * The "not before" dates, one per mapped account.
   *
   * Mounted after every render and disposed first, because this list is
   * rebuilt whole on each refresh: each mount puts listeners on the document
   * (the calendar panel is replaced on every repaint, so binding to it
   * directly is how a listener gets forgotten), and re-rendering without
   * disposing would leave a set behind every time.
   */
  private mountDateFields(): void {
    this.dateFields.forEach((f) => f.dispose());
    this.dateFields = [];
    for (const input of this.root.querySelectorAll<HTMLInputElement>('[id^="bsFrom-"]')) {
      const account = this.connections.flatMap((c) => c.accounts)
        .find((a) => `bsFrom-${a.id}` === input.id);
      input.value = (account?.importFrom ?? '').slice(0, 10);
      input.title = 'Ignore entries dated earlier than this. Empty imports everything the bank offers.';
      const button = this.root.querySelector<HTMLElement>(`[data-date-open="${CSS.escape(input.id)}"]`);
      if (button) this.dateFields.push(mountDateField(input, button));
    }
  }

  private connectionHtml(connection: ConnectionDto): string {
    const accounts = connection.accounts;
    const anyMapped = accounts.some(isMapped);
    const consent = consentHtml(connection.expiresAt, this.consentWarnDays);
    const capped = connection.syncsToday >= this.syncLimit;
    const syncTitle = !anyMapped
      ? 'Map at least one account first'
      : capped
        ? `This bank's ${this.syncLimit} fetches for today are used up`
        : 'Fetch new transactions for every mapped account on this bank';
    const admin = this.isAdmin;
    return `
      <div class="cf-card cf-card--bleed mb-3" data-conn="${connection.id}">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 pb-2">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="fw-semibold">${escapeHtml(connection.aspspName || 'Bank')}</span>
            <span class="badge text-bg-light">${escapeHtml(connection.aspspCountry)}</span>
            ${statusBadgeHtml(connection.status)}
            ${connection.status === 'error' && connection.errorMsg
              ? `<span class="text-danger small">${escapeHtml(connection.errorMsg)}</span>` : ''}
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            ${consent}
            <span class="badge ${capped ? 'text-bg-warning' : 'text-bg-light'}"
              title="Fetches used today against the ${this.syncLimit} a day this bank allows for the whole connection — every account synced is one fetch"
            >${connection.syncsToday}/${this.syncLimit} fetches today</span>
            <button class="btn btn-outline-primary btn-sm" type="button" data-sync-conn="${connection.id}"
              ${anyMapped && !capped ? '' : 'disabled'}
              title="${escapeHtml(syncTitle)}"
            >Sync bank</button>
            ${admin ? `<button class="btn btn-outline-secondary btn-sm" type="button" data-reconnect="${connection.id}"
              title="Renew the consent at the bank. Nothing is imported twice.">Reconnect</button>` : ''}
            ${admin ? `<button class="btn btn-outline-danger btn-sm" type="button" data-disconnect="${connection.id}">Disconnect</button>` : ''}
          </div>
        </div>
        <div class="px-3 pb-3">
          ${accounts.length === 0
            ? '<p class="text-secondary small mb-0">No accounts were granted on this connection.</p>'
            : accounts.map((a) => this.feedAccountHtml(a, capped)).join('')}
        </div>
      </div>`;
  }

  /**
   * One feed account: what the bank calls it, where it books to, and when it
   * last ran.
   *
   * A row rather than a table cell per field, because the mapping is three
   * controls of very different shapes — two account trees and a date — and a
   * five-column table of them is unreadable the moment an account name is
   * longer than "Cash".
   */
  private feedAccountHtml(account: BankAccountDto, capped: boolean): string {
    const mapped = isMapped(account);
    const flag = account.currency ? flagSvg(account.currency) : '';
    const title = account.name || account.iban || 'Account';
    const detail = [
      account.iban ?? '',
      account.currency ?? '',
      account.lastSynced ? `last synced ${formatDateTime(account.lastSynced)}` : 'never synced',
      account.lastTxnDate ? `newest entry ${account.lastTxnDate.slice(0, 10)}` : '',
    ].filter((part) => part !== '').join(' · ');
    const syncTitle = !mapped
      ? 'Map this account first'
      : capped
        ? `This bank's ${this.syncLimit} fetches for today are used up`
        : 'Fetch new transactions for this account';

    return `
      <div class="wl-bank-acct" data-ba="${account.id}">
        <div class="wl-bank-acct__head">
          <div>
            <div class="fw-semibold">${flag} ${escapeHtml(title)}</div>
            <div class="text-secondary small">${escapeHtml(detail)}</div>
          </div>
          <button class="btn btn-outline-primary btn-sm" type="button" data-sync-acct="${account.id}"
            ${mapped && !capped ? '' : 'disabled'} title="${escapeHtml(syncTitle)}">Sync</button>
        </div>
        ${this.isAdmin ? `
        <div class="wl-bank-acct__map">
          ${notchedFieldHtml('Ledger account', accountButtonHtml(
            this.accounts, account.ledgerAccountId, 'ledger', 'The account that *is* this bank account'))}
          ${notchedFieldHtml('Uncategorised', accountButtonHtml(
            this.accounts, account.suspenseAccountId, 'suspense', 'Where the other leg goes when no rule matches'))}
          ${notchedFieldHtml('Not before', dateFieldHtml(`bsFrom-${account.id}`, 'Not before'))}
          <button class="btn btn-outline-secondary btn-sm" type="button" data-save-map>Save mapping</button>
        </div>`
          : `<div class="text-secondary small">${mapped
            ? `Books into ${escapeHtml(accountPath(this.accounts, account.ledgerAccountId!))}`
            : 'Not mapped to a ledger account yet.'}</div>`}
      </div>`;
  }

  private wireConnections(): void {
    const container = this.root.querySelector<HTMLElement>('#bsConnections')!;

    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-sync-conn]')) {
      button.addEventListener('click', () => void this.runSync({ connectionId: button.dataset['syncConn']! }, button));
    }
    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-sync-acct]')) {
      button.addEventListener('click', () => void this.runSync({ bankAccountId: button.dataset['syncAcct']! }, button));
    }
    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-reconnect]')) {
      button.addEventListener('click', () => void this.reconnect(button.dataset['reconnect']!, button));
    }
    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-disconnect]')) {
      button.addEventListener('click', () => void this.disconnect(button.dataset['disconnect']!));
    }
    for (const row of container.querySelectorAll<HTMLElement>('.wl-bank-acct')) {
      this.wireMappingRow(row);
    }
  }

  private wireMappingRow(row: HTMLElement): void {
    for (const role of ['ledger', 'suspense'] as const) {
      const button = row.querySelector<HTMLElement>(`[data-account-btn="${role}"]`);
      button?.addEventListener('click', (event) => {
        openAccountPicker(event.currentTarget as HTMLElement, {
          // The whole chart, not only the bookable leaves — the folders are
          // what make it a tree. A folder toggles rather than being chosen,
          // so a placeholder can never be picked here.
          accounts: this.accounts, prefs: this.client.prefs,
          selectedId: button.dataset['accountId'] ?? '',
          onPick: (id) => {
            button.dataset['accountId'] = id;
            paintAccountButton(button, this.accounts, id);
          },
        });
      });
    }
    row.querySelector<HTMLButtonElement>('[data-save-map]')?.addEventListener('click', (event) => {
      void this.saveMapping(row, event.currentTarget as HTMLButtonElement);
    });
  }

  private async saveMapping(row: HTMLElement, button: HTMLButtonElement): Promise<void> {
    const ledgerAccountId = row.querySelector<HTMLElement>('[data-account-btn="ledger"]')?.dataset['accountId'] ?? '';
    const suspenseAccountId = row.querySelector<HTMLElement>('[data-account-btn="suspense"]')?.dataset['accountId'] ?? '';
    const importFrom = row.querySelector<HTMLInputElement>('[data-import-from]')?.value ?? '';

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Saving…';
    const res = await apiFetch('/apps/webledger/bank/map', {
      method: 'POST', token: this.client.token,
      body: { bankAccountId: row.dataset['ba'], ledgerAccountId, suspenseAccountId, importFrom },
    }).catch(() => undefined);

    if (!res?.ok) {
      button.disabled = false;
      button.textContent = original;
      this.notice(await failureMessage(res, 'Could not save the mapping.'));
      return;
    }
    // Re-read rather than patch in place: saving a mapping is what makes the
    // Sync buttons on this row live, and those are decided server-side from
    // the same row we just changed.
    this.notice('Mapping saved.', 'success');
    await this.refreshConnections();
  }

  private async reconnect(connectionId: string, button: HTMLButtonElement): Promise<void> {
    const connection = this.connections.find((c) => c.id === connectionId);
    if (!connection) return;
    await this.startConsent(button, {
      connectionId,
      aspspName: connection.aspspName,
      country: connection.aspspCountry,
      psuType: connection.psuType ?? 'personal',
    });
  }

  private async disconnect(connectionId: string): Promise<void> {
    const connection = this.connections.find((c) => c.id === connectionId);
    const name = connection?.aspspName ?? 'this bank';
    // Spelled out rather than "Are you sure?": the thing people actually fear
    // here is losing the transactions, and they do not.
    const go = await askConfirm({
      title: `Disconnect ${name}?`,
      message: [
        'Transactions that have already been imported are kept, and so is the record of which '
          + 'bank entry each one came from — so reconnecting later imports nothing twice.',
        'What goes away is the consent and the account mappings.',
      ],
      confirm: 'Disconnect', cancel: 'Stay connected', tone: 'danger',
    });
    if (!go) return;

    const res = await apiFetch('/apps/webledger/bank/disconnect', {
      method: 'POST', token: this.client.token, body: { connectionId },
    }).catch(() => undefined);
    if (!res?.ok) {
      this.notice('Could not disconnect this bank.');
      return;
    }
    this.notice(`Disconnected ${name}. Imported transactions were kept.`, 'success');
    await this.refreshConnections();
  }

  // ---------------------------------------------------------------- consent

  private wireConnectForm(): void {
    const root = this.root;
    const countrySelect = root.querySelector<HTMLSelectElement>('#bsCountry')!;
    const bankSelect = root.querySelector<HTMLSelectElement>('#bsBank')!;
    const connectButton = root.querySelector<HTMLButtonElement>('#bsConnect')!;
    const listButton = root.querySelector<HTMLButtonElement>('#bsListBanks')!;

    const reset = (): void => {
      bankSelect.innerHTML = '<option value="">List banks first…</option>';
      bankSelect.disabled = true;
      connectButton.disabled = true;
    };
    countrySelect.addEventListener('change', reset);
    bankSelect.addEventListener('change', () => { connectButton.disabled = !bankSelect.value; });

    listButton.addEventListener('click', () => {
      void (async () => {
        listButton.disabled = true;
        listButton.textContent = 'Listing…';
        this.clearNotice();
        const res = await apiFetch(
          `/apps/webledger/bank/aspsps?country=${encodeURIComponent(countrySelect.value)}`,
          { token: this.client.token },
        ).catch(() => undefined);
        listButton.disabled = false;
        listButton.textContent = 'List banks';

        if (!res?.ok) {
          const message = await failureMessage(res, 'Could not reach the bank directory.');
          reset();
          this.notice(message);
          return;
        }
        const { aspsps } = (await res.json()) as { aspsps: Array<{ name: string }> };
        if (aspsps.length === 0) {
          bankSelect.innerHTML = '<option value="">No banks found for this country</option>';
          bankSelect.disabled = true;
          connectButton.disabled = true;
          return;
        }
        bankSelect.innerHTML = '<option value="">— choose a bank —</option>'
          + aspsps.map((a) => `<option value="${escapeHtml(a.name)}">${escapeHtml(a.name)}</option>`).join('');
        bankSelect.disabled = false;
        connectButton.disabled = true;
        this.notify(`${aspsps.length} banks available in ${countrySelect.value}.`);
      })();
    });

    connectButton.addEventListener('click', () => {
      void this.startConsent(connectButton, {
        aspspName: bankSelect.value,
        country: countrySelect.value,
        psuType: root.querySelector<HTMLSelectElement>('#bsPsu')!.value,
      });
    });
  }

  /**
   * Ask for an authorisation URL and go there.
   *
   * The navigation off-site is the whole point of the button, and the card
   * says so above it, so it is not confirmed again — but it is the only place
   * in webledger that hands the browser to a third party, which is why it is
   * one named method rather than repeated inline for connect and reconnect.
   */
  private async startConsent(button: HTMLButtonElement, body: {
    aspspName: string; country: string; psuType: string; connectionId?: string;
  }): Promise<void> {
    if (!body.aspspName) {
      this.notice('Choose a bank first.');
      return;
    }
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Contacting your bank…';
    this.clearNotice();
    const res = await apiFetch('/apps/webledger/bank/connect', {
      method: 'POST', token: this.client.token, body,
    }).catch(() => undefined);

    if (res?.ok) {
      const { url } = (await res.json()) as { url: string };
      if (url) {
        window.location.assign(url);
        return;
      }
    }
    const message = await failureMessage(res, 'Could not start the connection.');
    button.disabled = false;
    button.textContent = original;
    this.notice(message);
  }

  // ------------------------------------------------------------------ sync

  private wireSyncAll(): void {
    const button = this.root.querySelector<HTMLButtonElement>('#bsSyncAll')!;
    button.addEventListener('click', () => void this.runSync({}, button));
    this.root.querySelector<HTMLButtonElement>('#bsApplyRules')!
      .addEventListener('click', (event) => void this.applyRules(event.currentTarget as HTMLButtonElement));
  }

  private async runSync(body: { bankAccountId?: string; connectionId?: string }, button: HTMLButtonElement): Promise<void> {
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Syncing…';
    this.clearNotice();
    this.notify('Fetching transactions from the bank…');

    const res = await apiFetch('/apps/webledger/bank/sync', { method: 'POST', token: this.client.token, body })
      .catch(() => undefined);
    if (!res?.ok) {
      const message = await failureMessage(res, 'The sync could not be started.');
      button.disabled = false;
      button.textContent = original;
      this.renderSyncFailure(message);
      return;
    }
    this.renderSyncResult((await res.json()) as SyncResponse);
    // Re-render restores every button's state and the per-account counters,
    // which have just moved.
    await this.refreshConnections();
  }

  private renderSyncFailure(message: string): void {
    this.root.querySelector<HTMLElement>('#bsSyncResult')!.innerHTML =
      `<div class="alert alert-danger py-2 small">${escapeHtml(message)}</div>`;
    this.notify(message);
  }

  /**
   * What the run did, per account and in total.
   *
   * `skipped` is deliberately given a plain-English name here — "already
   * present" — because on any run after the first it is the big number, and
   * "skipped" reads like something went wrong when it is the de-duplication
   * working exactly as intended.
   */
  private renderSyncResult(data: SyncResponse): void {
    const s = data.summary;
    const level = s.errors > 0 ? 'warning' : 'success';
    const parts = [
      `${s.added} added`,
      `${s.skipped} already present`,
      ...(s.enriched > 0 ? [`${s.enriched} filled in with bank details`] : []),
      ...(s.capped > 0 ? [`${s.capped} at the daily limit`] : []),
      ...(s.expired > 0 ? [`${s.expired} needing a reconnect`] : []),
      ...(s.errors > 0 ? [`${s.errors} failed`] : []),
    ];
    const summary = `Synced ${s.accounts} account${s.accounts === 1 ? '' : 's'}: ${parts.join(', ')}.`;
    const lines = data.results.map((r) => {
      const mark = { ok: '✓', skipped: '–', capped: '⏸', expired: '⚠', error: '✗' }[r.status] ?? '•';
      const detail = r.status === 'ok'
        ? `${r.added} added, ${r.skipped} already present`
          + (r.enriched > 0 ? `, ${r.enriched} filled in` : '')
        : escapeHtml(r.message || r.status);
      return `<li><span aria-hidden="true">${mark}</span> <strong>${escapeHtml(r.label)}</strong> — ${detail}</li>`;
    }).join('');

    this.root.querySelector<HTMLElement>('#bsSyncResult')!.innerHTML =
      `<div class="alert alert-${level} py-2 small">
        <div class="fw-semibold mb-1">${escapeHtml(summary)}</div>
        <ul class="mb-0">${lines}</ul>
      </div>`;
    this.notify(summary);
  }

  // ----------------------------------------------------------------- rules

  private async refreshRules(): Promise<void> {
    const container = this.root.querySelector<HTMLElement>('#bsRules')!;
    if (knownOffline()) {
      container.innerHTML = '<p class="text-secondary small mb-0">Rules need a connection.</p>';
      return;
    }
    const res = await apiFetch('/apps/webledger/bank/rules', { token: this.client.token }).catch(() => undefined);
    if (!res?.ok) {
      container.innerHTML = '<p class="text-secondary small mb-0">Could not load the rules.</p>';
      return;
    }
    this.rules = ((await res.json()) as { rules: RuleDto[] }).rules;
    this.renderRules();
    this.renderRuleForm();
  }

  private renderRules(): void {
    const container = this.root.querySelector<HTMLElement>('#bsRules')!;
    if (this.rules.length === 0) {
      container.innerHTML = '<p class="text-secondary small mb-0">No rules yet. '
        + 'Without one, every imported entry books its other leg to the account\'s uncategorised account.</p>';
      return;
    }
    const rows = this.rules.map((rule) => `
      <tr${rule.enabled ? '' : ' class="wl-bank-rule--off"'}>
        <td class="text-end">${rule.priority}</td>
        <td>
          <span class="text-secondary">${escapeHtml(MATCH_FIELD_LABELS[rule.matchField] ?? rule.matchField)}</span>
          ${escapeHtml(rule.matchType)} <code>${escapeHtml(rule.pattern)}</code>
        </td>
        <td>${inlineAccountIcon(this.accounts, rule.targetAccountId)}${escapeHtml(rule.accountName ?? '(deleted account)')}</td>
        <td>${rule.enabled
          ? '<span class="badge text-bg-light">On</span>'
          : '<span class="badge text-bg-secondary">Off</span>'}</td>
        <td class="text-end text-nowrap">${this.isAdmin ? `
          <button class="btn btn-outline-secondary btn-sm" type="button" data-rule-edit="${rule.id}">Edit</button>
          <button class="btn btn-outline-danger btn-sm" type="button" data-rule-delete="${rule.id}">Delete</button>` : ''}
        </td>
      </tr>`).join('');

    container.innerHTML = `
      <div class="table-responsive">
        <table class="table table-sm align-middle mb-0">
          <thead><tr>
            <th class="text-end">Priority</th><th>When</th><th>Books the other leg to</th><th>Enabled</th><th></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;

    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-rule-edit]')) {
      button.addEventListener('click', () => this.editRule(button.dataset['ruleEdit']!));
    }
    for (const button of container.querySelectorAll<HTMLButtonElement>('[data-rule-delete]')) {
      button.addEventListener('click', () => void this.deleteRule(button.dataset['ruleDelete']!));
    }
  }

  private renderRuleForm(): void {
    const container = this.root.querySelector<HTMLElement>('#bsRuleForm')!;
    if (!this.isAdmin) {
      container.innerHTML = '';
      return;
    }
    const rule = this.editingRule;
    const option = (value: string, label: string, selected: string): string =>
      `<option value="${value}"${value === selected ? ' selected' : ''}>${escapeHtml(label)}</option>`;

    container.innerHTML = `
      <hr>
      <form class="row g-2 align-items-end" id="bsRuleFields">
        <div class="col-sm-3">
          <label class="form-label mb-1 small" for="bsRuleField">Match on</label>
          <select class="form-select form-select-sm" id="bsRuleField">
            ${option('any', MATCH_FIELD_LABELS['any']!, rule?.matchField ?? 'any')}
            ${option('description', MATCH_FIELD_LABELS['description']!, rule?.matchField ?? 'any')}
            ${option('counterparty', MATCH_FIELD_LABELS['counterparty']!, rule?.matchField ?? 'any')}
          </select>
        </div>
        <div class="col-sm-2">
          <label class="form-label mb-1 small" for="bsRuleType">How</label>
          <select class="form-select form-select-sm" id="bsRuleType">
            ${option('contains', 'contains', rule?.matchType ?? 'contains')}
            ${option('equals', 'equals', rule?.matchType ?? 'contains')}
            ${option('regex', 'regular expression', rule?.matchType ?? 'contains')}
          </select>
        </div>
        <div class="col-sm-3">
          <label class="form-label mb-1 small" for="bsRulePattern">Pattern</label>
          <input class="form-control form-control-sm" id="bsRulePattern" placeholder="e.g. SPAR"
            value="${escapeHtml(rule?.pattern ?? '')}" required>
        </div>
        <div class="col-sm-2">
          <label class="form-label mb-1 small" for="bsRuleTarget">Books to</label>
          ${accountButtonHtml(this.accounts, this.ruleTargetId || null, 'rule-target', 'The account the other leg goes to')}
        </div>
        <div class="col-sm-1">
          <label class="form-label mb-1 small" for="bsRulePriority">Priority</label>
          <input class="form-control form-control-sm" id="bsRulePriority" type="number"
            value="${rule?.priority ?? 0}" title="Checked in ascending order; the first match wins.">
        </div>
        <div class="col-sm-1 d-flex gap-1">
          <button class="btn btn-primary btn-sm flex-fill" type="submit">${rule ? 'Save' : 'Add'}</button>
          ${rule ? '<button class="btn btn-outline-secondary btn-sm" type="button" id="bsRuleCancel">Cancel</button>' : ''}
        </div>
        <div class="col-12">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="bsRuleEnabled" ${rule === undefined || rule.enabled ? 'checked' : ''}>
            <label class="form-check-label small" for="bsRuleEnabled">Enabled — a disabled rule is kept but never matches</label>
          </div>
        </div>
      </form>`;

    const targetButton = container.querySelector<HTMLElement>('[data-account-btn="rule-target"]')!;
    targetButton.addEventListener('click', (event) => {
      openAccountPicker(event.currentTarget as HTMLElement, {
        accounts: this.accounts, prefs: this.client.prefs,
        selectedId: this.ruleTargetId,
        onPick: (id) => {
          this.ruleTargetId = id;
          paintAccountButton(targetButton, this.accounts, id);
        },
      });
    });
    container.querySelector<HTMLElement>('#bsRuleCancel')?.addEventListener('click', () => {
      this.editingRule = undefined;
      this.ruleTargetId = '';
      this.renderRuleForm();
    });
    container.querySelector<HTMLFormElement>('#bsRuleFields')!.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.saveRule();
    });
  }

  private editRule(id: string): void {
    const rule = this.rules.find((r) => r.id === id);
    if (!rule) return;
    this.editingRule = rule;
    this.ruleTargetId = rule.targetAccountId;
    this.renderRuleForm();
    this.root.querySelector<HTMLElement>('#bsRuleForm')!.scrollIntoView({ block: 'center', behavior: 'instant' });
    this.notify(`Editing the rule for “${rule.pattern}”.`);
  }

  private async saveRule(): Promise<void> {
    const container = this.root.querySelector<HTMLElement>('#bsRuleForm')!;
    const body = {
      matchField: container.querySelector<HTMLSelectElement>('#bsRuleField')!.value,
      matchType: container.querySelector<HTMLSelectElement>('#bsRuleType')!.value,
      pattern: container.querySelector<HTMLInputElement>('#bsRulePattern')!.value.trim(),
      targetAccountId: this.ruleTargetId,
      priority: Number(container.querySelector<HTMLInputElement>('#bsRulePriority')!.value) || 0,
      enabled: container.querySelector<HTMLInputElement>('#bsRuleEnabled')!.checked,
    };
    if (!body.pattern) { this.notice('Enter a pattern.'); return; }
    if (!body.targetAccountId) { this.notice('Choose the account the other leg should go to.'); return; }

    const editing = this.editingRule;
    const res = await apiFetch(
      editing ? `/apps/webledger/bank/rules/${editing.id}` : '/apps/webledger/bank/rules',
      { method: editing ? 'PUT' : 'POST', token: this.client.token, body },
    ).catch(() => undefined);

    if (!res?.ok) {
      this.notice(await failureMessage(res, 'Could not save the rule.'));
      return;
    }
    this.editingRule = undefined;
    this.ruleTargetId = '';
    this.notify(editing ? 'Rule saved.' : 'Rule added.');
    await this.refreshRules();
  }

  private async deleteRule(id: string): Promise<void> {
    const rule = this.rules.find((r) => r.id === id);
    if (!rule) return;
    const go = await askConfirm({
      title: `Delete the rule for “${rule.pattern}”?`,
      message: 'Transactions it has already categorised keep the account it chose.',
      confirm: 'Delete the rule', cancel: 'Keep it', tone: 'danger',
    });
    if (!go) return;
    const res = await apiFetch(`/apps/webledger/bank/rules/${id}`, { method: 'DELETE', token: this.client.token })
      .catch(() => undefined);
    if (!res?.ok) { this.notice('Could not delete the rule.'); return; }
    if (this.editingRule?.id === id) {
      this.editingRule = undefined;
      this.ruleTargetId = '';
    }
    this.notify('Rule deleted.');
    await this.refreshRules();
  }

  /**
   * Re-run the rules over imports still sitting in an uncategorised account.
   *
   * The counterpart to adding a rule after the fact: without this, a rule
   * only ever affects entries imported *after* it was written, which is
   * rarely what the person writing it meant.
   */
  private async applyRules(button: HTMLButtonElement): Promise<void> {
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Applying…';
    const res = await apiFetch('/apps/webledger/bank/rules/apply', { method: 'POST', token: this.client.token })
      .catch(() => undefined);
    button.disabled = false;
    button.textContent = original;

    if (!res?.ok) { this.notice('Could not apply the rules.'); return; }
    const { recategorised, scanned } = (await res.json()) as { recategorised: number; scanned: number };
    const message = scanned === 0
      ? 'Nothing is sitting in an uncategorised account.'
      : `Re-categorised ${recategorised} of ${scanned} uncategorised import${scanned === 1 ? '' : 's'}.`;
    this.root.querySelector<HTMLElement>('#bsSyncResult')!.innerHTML =
      `<div class="alert alert-success py-2 small">${escapeHtml(message)}</div>`;
    this.notify(message);
  }
}

// ----------------------------------------------------------------- helpers

function isMapped(account: BankAccountDto): boolean {
  return Boolean(account.ledgerAccountId && account.suspenseAccountId);
}

function offlineNoticeHtml(): string {
  return '<div class="alert alert-warning py-2 small mb-0">'
    + 'Bank sync needs a connection — connecting to a bank, and fetching from one, both go out to the internet. '
    + 'Everything already imported is on the Transactions page and works offline as usual.</div>';
}

/**
 * The connection's state, as a word and not only a colour (invariant #11).
 *
 * `text-bg-*` carries the colour; the word inside carries the meaning, so
 * this reads the same in greyscale and to a screen reader.
 */
function statusBadgeHtml(status: string): string {
  const tone: Record<string, string> = {
    active: 'text-bg-success', pending: 'text-bg-secondary',
    error: 'text-bg-danger', expired: 'text-bg-warning',
  };
  const label: Record<string, string> = {
    active: 'Authorised', pending: 'Awaiting authorisation',
    error: 'Failed', expired: 'Consent expired',
  };
  return `<span class="badge ${tone[status] ?? 'text-bg-secondary'}">${escapeHtml(label[status] ?? status)}</span>`;
}

/**
 * How long the consent has left.
 *
 * A bank consent lapses on a date the bank chose, and when it does every sync
 * stops with an authorisation error that says nothing useful. Warning ahead of
 * it — `EB_CONSENT_WARN_DAYS` — is the difference between reconnecting on a
 * calm Tuesday and discovering it while chasing a missing transaction.
 */
function consentHtml(expiresAt: string | null, warnDays: number): string {
  if (!expiresAt) return '';
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return '';
  const days = Math.ceil((expiry.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return '<span class="badge text-bg-danger">Consent expired — reconnect</span>';
  if (days <= warnDays) {
    return `<span class="badge text-bg-warning">Consent expires in ${days} day${days === 1 ? '' : 's'}</span>`;
  }
  return `<span class="text-secondary small">Consent until ${escapeHtml(expiry.toLocaleDateString())}</span>`;
}

function formatDateTime(iso: string): string {
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? iso : when.toLocaleString();
}

/** The picker trigger, in the same shape the split table uses — one control vocabulary across the app. */
function accountButtonHtml(
  accounts: readonly AccountDto[], accountId: string | null, role: string, hint: string,
): string {
  const path = accountId ? accountPath(accounts, accountId) : '';
  return `<button type="button" class="wl-nf__ctl wl-acct-btn" data-account-btn="${role}"
    data-account-id="${escapeHtml(accountId ?? '')}" title="${escapeHtml(path || hint)}"
    aria-label="${escapeHtml(path ? `Account: ${path}` : hint)}">
    <span data-account-icon>${accountId ? inlineAccountIcon(accounts, accountId) : ''}</span>
    <span class="wl-acct-btn__name${path ? '' : ' wl-acct-btn__name--empty'}">${escapeHtml(path || 'Choose account…')}</span>
    <span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>
  </button>`;
}

function paintAccountButton(button: HTMLElement, accounts: readonly AccountDto[], accountId: string): void {
  const path = accountPath(accounts, accountId);
  button.querySelector<HTMLElement>('[data-account-icon]')!.innerHTML = inlineAccountIcon(accounts, accountId);
  const name = button.querySelector<HTMLElement>('.wl-acct-btn__name')!;
  name.textContent = path;
  name.classList.remove('wl-acct-btn__name--empty');
  button.title = path;
  button.setAttribute('aria-label', `Account: ${path}`);
}
